/* PCIS — Edit credentials drawer (opened from the Manage accounts table).
   Loads the account's current credentials (GET courier-accounts/{code}) and lets
   an admin update them (PATCH courier-accounts/{code} with {credentials}). PATCH
   is partial/merged, so only the fields the user fills are sent. After a successful
   update the adapter registry is refreshed so PCIS picks up the change immediately.

   Encrypted values come back with an `ENC:` prefix — those can't be shown, so the
   field is left blank with a "leave blank to keep" hint; a blank field is not sent. */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Typography} from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import {SideDrawer, useToast} from '../components'
import {Field} from '../m0-onboard/steps/fieldKit'
import {resolveCourierSchemaKey, getCourierSchema} from '../m0-onboard/diCourierSchemas'
import {useLazyGetPcisCourierAccountQuery, useUpdatePcisCourierAccountMutation, useRefreshAdapterRegistryMutation} from '../../../redux/supplyApiSlice'

const YN = ['Yes', 'No']
const humanize = (k) =>
  String(k || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
const isEnc = (v) => typeof v === 'string' && v.startsWith('ENC:')

export default function PcisEditCredentialsDrawer({open, onClose, account}) {
  const toast = useToast()
  const [fetchAcct, {data: acctRaw, isFetching}] = useLazyGetPcisCourierAccountQuery()
  const [updateAcct, {isLoading: saving}] = useUpdatePcisCourierAccountMutation()
  const [refreshAdapter] = useRefreshAdapterRegistryMutation()

  const [values, setValues] = useState({}) // {key: string|boolean}
  const [extras, setExtras] = useState([]) // [{key, value}]

  const acct = acctRaw || null
  const currentCreds = useMemo(() => acct?.credentials || {}, [acct])

  /* which fields to render: the account's actual credential keys, else the
     courier's DI schema keys as a fallback */
  const schema = account ? getCourierSchema(resolveCourierSchemaKey(account.courier_code)) : null
  const fields = useMemo(() => {
    const keys = Object.keys(currentCreds)
    if (keys.length) {
      return keys.map((k) => ({key: k, label: humanize(k), type: typeof currentCreds[k] === 'boolean' ? 'toggle' : 'text', enc: isEnc(currentCreds[k])}))
    }
    return (schema?.fields || []).map((f) => ({key: f.key, label: f.label, type: f.type === 'toggle' ? 'toggle' : 'text', enc: false}))
  }, [currentCreds, schema])

  /* load account + prefill when the drawer opens */
  useEffect(() => {
    if (open && account?.account_code) fetchAcct(account.account_code)
    if (!open) {
      setValues({})
      setExtras([])
    }
  }, [open, account, fetchAcct])

  useEffect(() => {
    if (!acct) return
    const init = {}
    Object.entries(currentCreds).forEach(([k, v]) => {
      if (typeof v === 'boolean') init[k] = v
      else init[k] = isEnc(v) || v == null ? '' : String(v)
    })
    setValues(init)
  }, [acct, currentCreds])

  const setVal = (k, v) => setValues((p) => ({...p, [k]: v}))
  const addExtra = () => setExtras((x) => [...x, {key: '', value: ''}])
  const updateExtra = (i, patch) => setExtras((x) => x.map((e, idx) => (idx === i ? {...e, ...patch} : e)))
  const removeExtra = (i) => setExtras((x) => x.filter((_, idx) => idx !== i))

  const handleClose = () => {
    setValues({})
    setExtras([])
    onClose && onClose()
  }

  const submit = async () => {
    const credentials = {}
    fields.forEach((f) => {
      const v = values[f.key]
      if (f.type === 'toggle') credentials[f.key] = !!v
      else if (String(v ?? '').trim() !== '') credentials[f.key] = v // blank text = leave unchanged
    })
    extras.forEach((x) => {
      const k = String(x.key || '').trim()
      const v = String(x.value || '').trim()
      if (k && v) credentials[k] = v
    })
    if (!Object.keys(credentials).length) return toast('Enter at least one credential to update.', 'error')
    try {
      await updateAcct({accountCode: account.account_code, body: {credentials}}).unwrap()
      await refreshAdapter().unwrap().catch(() => {}) // best-effort: don't fail the save if refresh hiccups
      toast(`Credentials updated for ${account.account_code}.`, 'success')
      handleClose()
    } catch (e) {
      toast(e?.message || 'Could not update credentials.', 'error')
    }
  }

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      title='Edit credentials'
      width={560}
      footer={
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%'}}>
          <button type='button' className='btn' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='button'
            disabled={saving || isFetching}
            onClick={submit}
            style={{border: 'none', borderRadius: '10px', padding: '9px 22px', fontWeight: 700, fontSize: 13, color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, backgroundImage: 'linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)', boxShadow: '0 6px 16px rgba(32,101,209,.35)'}}
          >
            {saving ? 'Saving…' : 'Save credentials'}
          </button>
        </div>
      }
    >
      {account ? (
        <Box sx={{border: 1, borderColor: 'divider', borderRadius: '10px', bgcolor: 'var(--row-alt, #F8FAFC)', p: 1.5, mb: 2}}>
          <Typography sx={{fontSize: 13.5, fontWeight: 800}}>{account.display_name || account.account_code}</Typography>
          <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 0.25}}>
            {account.courier_code} · {account.account_code} · cp_id {account.cp_id}
          </Typography>
        </Box>
      ) : null}

      {isFetching ? (
        <Typography sx={{fontSize: 13, color: 'text.secondary', py: 2}}>Loading current credentials…</Typography>
      ) : (
        <>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px'}}>
            {fields.map((f) =>
              f.type === 'toggle' ? (
                <Field key={f.key} label={f.label} type='select' options={YN} value={values[f.key] ? 'Yes' : 'No'} onChange={(v) => setVal(f.key, v === 'Yes')} opt />
              ) : (
                <Field
                  key={f.key}
                  label={f.label}
                  value={values[f.key]}
                  onChange={(v) => setVal(f.key, v)}
                  ph={f.enc ? 'Encrypted — leave blank to keep' : ''}
                  help={f.enc ? 'Stored encrypted; leave blank to keep the existing value.' : undefined}
                  opt
                />
              )
            )}
          </div>

          {/* extra / new credential keys */}
          {extras.length ? (
            <Box sx={{mt: 1}}>
              {extras.map((x, i) => (
                <Box key={i} sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 34px', gap: '10px', alignItems: 'start', mb: 1}}>
                  <input className='input' placeholder='Key' value={x.key} onChange={(e) => updateExtra(i, {key: e.target.value})} />
                  <input className='input' placeholder='Value' value={x.value} onChange={(e) => updateExtra(i, {value: e.target.value})} />
                  <button type='button' className='iconbtn' onClick={() => removeExtra(i)} title='Remove' style={{border: '1px solid var(--border)', borderRadius: 8, background: '#fff', cursor: 'pointer', height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <DeleteOutlineOutlinedIcon style={{fontSize: 18}} />
                  </button>
                </Box>
              ))}
            </Box>
          ) : null}
          <button type='button' className='btn' onClick={addExtra} style={{marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6}}>
            <AddOutlinedIcon style={{fontSize: 17}} /> Add field
          </button>
        </>
      )}
    </SideDrawer>
  )
}

PcisEditCredentialsDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  account: PropTypes.object,
}
